import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import api from "@/lib/api";
import { PageHeader } from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Sticker, Save, Trash2, ArrowRight, Plus, Calculator, Package, Settings, Check } from "lucide-react";

export default function CalculadoraPegatinas() {
  const nav = useNavigate();
  const [config, setConfig] = useState(null);
  const [materiales, setMateriales] = useState([]);
  const [cotizaciones, setCotizaciones] = useState([]);
  const [clientes, setClientes] = useState([]);
  const [showAjustes, setShowAjustes] = useState(false);
  const [form, setForm] = useState({
    material_id: "",
    ancho_bobina_cm: 137,
    merma_pct: "",
    gutter_mm: 2,
    margen_pct: "",
    disenos: [{ nombre: "Diseño 1", ancho_cm: 8, alto_cm: 3, cantidad: 400, corte_unitario: false }],
    nombre_cotizacion: "",
    cliente_id: "",
    notas: "",
  });
  const [resultado, setResultado] = useState(null);
  const [saving, setSaving] = useState(false);

  const reloadConfig = () => api.get("/calculadora-pegatinas/config").then((r) => {
    setConfig(r.data.config);
    setMateriales(r.data.materiales || []);
    if ((r.data.materiales || []).length && !form.material_id) {
      setForm((f) => ({ ...f, material_id: r.data.materiales[0].id }));
    }
  });

  useEffect(() => {
    reloadConfig();
    api.get("/calculadora-pegatinas/cotizaciones").then((r) => setCotizaciones(r.data || []));
    api.get("/clientes").then((r) => setClientes(r.data || []));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Recalcular en cada cambio con debounce leve
  useEffect(() => {
    if (!form.material_id) return;
    const t = setTimeout(async () => {
      try {
        const payload = {
          material_id: form.material_id,
          ancho_bobina_cm: Number(form.ancho_bobina_cm),
          gutter_mm: Number(form.gutter_mm),
          merma_pct: form.merma_pct === "" ? null : Number(form.merma_pct),
          margen_pct: form.margen_pct === "" ? null : Number(form.margen_pct),
          disenos: form.disenos.map((d) => ({
            nombre: d.nombre,
            ancho_cm: Number(d.ancho_cm),
            alto_cm: Number(d.alto_cm),
            cantidad: Number(d.cantidad),
            corte_unitario: !!d.corte_unitario,
          })),
        };
        const r = await api.post("/calculadora-pegatinas/calcular", payload);
        setResultado(r.data);
      } catch (err) {
        // eslint-disable-next-line no-console
        console.warn("calc err", err?.response?.data);
        setResultado(null);
      }
    }, 250);
    return () => clearTimeout(t);
  }, [form]);

  const updateDiseno = (idx, patch) => {
    setForm((f) => ({ ...f, disenos: f.disenos.map((d, i) => (i === idx ? { ...d, ...patch } : d)) }));
  };
  const addDiseno = () => setForm((f) => ({
    ...f,
    disenos: [...f.disenos, { nombre: `Diseño ${f.disenos.length + 1}`, ancho_cm: 8, alto_cm: 3, cantidad: 400, corte_unitario: false }],
  }));
  const removeDiseno = (idx) => setForm((f) => ({ ...f, disenos: f.disenos.filter((_, i) => i !== idx) }));

  const guardar = async () => {
    if (!form.nombre_cotizacion.trim()) return toast.error("Ponle un nombre a la cotización");
    setSaving(true);
    try {
      const payload = {
        material_id: form.material_id,
        ancho_bobina_cm: Number(form.ancho_bobina_cm),
        gutter_mm: Number(form.gutter_mm),
        merma_pct: form.merma_pct === "" ? null : Number(form.merma_pct),
        margen_pct: form.margen_pct === "" ? null : Number(form.margen_pct),
        disenos: form.disenos.map((d) => ({
          nombre: d.nombre, ancho_cm: Number(d.ancho_cm), alto_cm: Number(d.alto_cm),
          cantidad: Number(d.cantidad), corte_unitario: !!d.corte_unitario,
        })),
        nombre: form.nombre_cotizacion,
        cliente_id: form.cliente_id || null,
        notas: form.notas || null,
      };
      const r = await api.post("/calculadora-pegatinas/cotizaciones", payload);
      toast.success(`Cotización guardada · ${r.data.resultado.precio_final}€`);
      const list = await api.get("/calculadora-pegatinas/cotizaciones");
      setCotizaciones(list.data || []);
    } catch (err) { toast.error(err?.response?.data?.detail || "Error"); }
    setSaving(false);
  };

  const convertir = async (cid) => {
    if (!window.confirm("¿Convertir esta cotización en una petición DNF nueva?")) return;
    try {
      const r = await api.post(`/calculadora-pegatinas/cotizaciones/${cid}/convertir-peticion`);
      toast.success(`Petición ${r.data.peticion_codigo} creada`);
      nav(`/peticiones/${r.data.peticion_id}`);
    } catch (err) { toast.error(err?.response?.data?.detail || "Error"); }
  };

  const eliminar = async (cid) => {
    if (!window.confirm("¿Eliminar cotización?")) return;
    await api.delete(`/calculadora-pegatinas/cotizaciones/${cid}`);
    setCotizaciones((c) => c.filter((x) => x.id !== cid));
  };

  return (
    <div className="p-6 max-w-[1700px] mx-auto">
      <PageHeader icon={Sticker} title="🏷️ Calculadora de pegatinas"
        description="Presupuesta tiradas de vinilo impreso al vuelo. Se guarda como cotización y se puede convertir en petición en 1 click." />

      <div className="grid lg:grid-cols-[420px_1fr] gap-4 mt-4">
        {/* Formulario */}
        <div className="bg-zinc-900/40 border border-zinc-800 rounded-xl p-4 space-y-3">
          <div>
            <Label className="text-xs">Material</Label>
            <Select value={form.material_id} onValueChange={(v) => setForm({ ...form, material_id: v })}>
              <SelectTrigger className="bg-zinc-950 border-zinc-800" data-testid="calc-material">
                <SelectValue placeholder="Selecciona…" />
              </SelectTrigger>
              <SelectContent className="bg-zinc-950 border-zinc-800 text-zinc-100 max-h-[300px]">
                {materiales.map((m) => {
                  const pvp = m.precio_venta_m2 ?? (Number(m.precio_m2 || m.precio_unitario || 0) + (config?.coste_impresion_m2 || 3.5));
                  return (
                    <SelectItem key={m.id} value={m.id}>{m.nombre} · {Number(pvp).toFixed(2)}€/m²</SelectItem>
                  );
                })}
              </SelectContent>
            </Select>
          </div>
          <div className="grid grid-cols-2 gap-2">
            <div>
              <Label className="text-xs">Ancho bobina (cm)</Label>
              <Input type="number" step="1" value={form.ancho_bobina_cm}
                onChange={(e) => setForm({ ...form, ancho_bobina_cm: e.target.value })}
                data-testid="calc-ancho-bobina" className="bg-zinc-950 border-zinc-800" />
            </div>
            <div>
              <Label className="text-xs">Merma % (auto)</Label>
              <Input type="number" step="0.5" value={form.merma_pct} placeholder={config?.merma_pct_default || "10"}
                onChange={(e) => setForm({ ...form, merma_pct: e.target.value })}
                data-testid="calc-merma" className="bg-zinc-950 border-zinc-800" />
            </div>
            <div>
              <Label className="text-xs">Gutter (mm)</Label>
              <Input type="number" step="0.5" value={form.gutter_mm}
                onChange={(e) => setForm({ ...form, gutter_mm: e.target.value })}
                className="bg-zinc-950 border-zinc-800" />
            </div>
            <div>
              <Label className="text-xs">Margen % (auto)</Label>
              <Input type="number" step="1" value={form.margen_pct} placeholder={config?.margen_pct_default || "40"}
                onChange={(e) => setForm({ ...form, margen_pct: e.target.value })}
                className="bg-zinc-950 border-zinc-800" />
            </div>
          </div>
          <div className="flex justify-end">
            <Button size="sm" variant="ghost" onClick={() => setShowAjustes(true)}
              className="h-7 text-xs text-zinc-400 hover:text-white" data-testid="calc-ajustes-btn">
              <Settings className="w-3 h-3 mr-1" /> Ajustes
            </Button>
          </div>

          {/* Diseños */}
          <div className="border-t border-zinc-800 pt-3">
            <div className="flex items-center justify-between mb-2">
              <Label className="text-xs font-semibold">Diseños</Label>
              <Button size="sm" variant="ghost" onClick={addDiseno} className="h-7 text-xs">
                <Plus className="w-3 h-3 mr-1" /> Añadir diseño
              </Button>
            </div>
            {form.disenos.map((d, idx) => (
              <div key={idx} className="bg-zinc-950/70 border border-zinc-800 rounded p-2 mb-2 space-y-1.5"
                data-testid={`calc-diseno-${idx}`}>
                <div className="flex items-center gap-1">
                  <Input value={d.nombre} onChange={(e) => updateDiseno(idx, { nombre: e.target.value })}
                    className="bg-zinc-900 border-zinc-800 h-7 text-xs" placeholder="Nombre" />
                  {form.disenos.length > 1 && (
                    <Button size="sm" variant="ghost" onClick={() => removeDiseno(idx)}
                      className="h-7 w-7 p-0 text-red-400"><Trash2 className="w-3 h-3" /></Button>
                  )}
                </div>
                <div className="grid grid-cols-3 gap-1">
                  <Input type="number" value={d.ancho_cm} step="0.1"
                    onChange={(e) => updateDiseno(idx, { ancho_cm: e.target.value })}
                    className="bg-zinc-900 border-zinc-800 h-7 text-xs" placeholder="Ancho cm" />
                  <Input type="number" value={d.alto_cm} step="0.1"
                    onChange={(e) => updateDiseno(idx, { alto_cm: e.target.value })}
                    className="bg-zinc-900 border-zinc-800 h-7 text-xs" placeholder="Alto cm" />
                  <Input type="number" value={d.cantidad}
                    onChange={(e) => updateDiseno(idx, { cantidad: e.target.value })}
                    className="bg-zinc-900 border-zinc-800 h-7 text-xs" placeholder="Cantidad" />
                </div>
                <label className="flex items-center gap-1 text-[11px] cursor-pointer">
                  <input type="checkbox" checked={d.corte_unitario}
                    onChange={(e) => updateDiseno(idx, { corte_unitario: e.target.checked })} />
                  Cortadas unitariamente (coste extra)
                </label>
              </div>
            ))}
          </div>

          {/* Guardar cotización */}
          <div className="border-t border-zinc-800 pt-3 space-y-2">
            <Input value={form.nombre_cotizacion}
              onChange={(e) => setForm({ ...form, nombre_cotizacion: e.target.value })}
              placeholder="Nombre cotización (ej: Cliente X - pegatinas Q4)"
              data-testid="calc-nombre-cotizacion"
              className="bg-zinc-900 border-zinc-800" />
            <Select value={form.cliente_id || "__none__"}
              onValueChange={(v) => setForm({ ...form, cliente_id: v === "__none__" ? "" : v })}>
              <SelectTrigger className="bg-zinc-900 border-zinc-800"><SelectValue placeholder="Cliente (opcional)" /></SelectTrigger>
              <SelectContent className="bg-zinc-950 border-zinc-800 text-zinc-100 max-h-[240px]">
                <SelectItem value="__none__">— Sin cliente —</SelectItem>
                {clientes.map((c) => <SelectItem key={c.id} value={c.id}>{c.nombre}</SelectItem>)}
              </SelectContent>
            </Select>
            <Textarea value={form.notas} onChange={(e) => setForm({ ...form, notas: e.target.value })}
              placeholder="Notas internas" className="bg-zinc-900 border-zinc-800 min-h-[50px] text-xs" />
            <Button onClick={guardar} disabled={saving || !resultado}
              data-testid="calc-guardar"
              className="w-full bg-amber-500 text-zinc-950 hover:bg-amber-400">
              <Save className="w-4 h-4 mr-1" /> Guardar cotización
            </Button>
          </div>
        </div>

        {/* Resultado + preview + cotizaciones */}
        <div className="space-y-4">
          {resultado ? (
            <ResultadoCard r={resultado} />
          ) : (
            <div className="bg-zinc-900/40 border border-zinc-800 rounded-xl p-8 text-center text-sm text-zinc-500">
              <Calculator className="w-8 h-8 mx-auto mb-2 opacity-40" />
              Completa el material y los diseños para ver el presupuesto.
            </div>
          )}

          {/* Cotizaciones guardadas */}
          <div className="bg-zinc-900/40 border border-zinc-800 rounded-xl p-4">
            <div className="flex items-center gap-2 mb-2">
              <Package className="w-4 h-4 text-amber-400" />
              <div className="text-sm font-semibold">Cotizaciones guardadas ({cotizaciones.length})</div>
            </div>
            <div className="space-y-1 max-h-[300px] overflow-y-auto">
              {cotizaciones.length === 0 && <div className="text-xs text-zinc-500 italic py-3">Aún no hay cotizaciones guardadas.</div>}
              {cotizaciones.map((c) => (
                <div key={c.id} className="bg-zinc-950/70 border border-zinc-800 rounded p-2 flex items-center gap-2 text-xs">
                  <div className="flex-1 min-w-0">
                    <div className="font-semibold truncate">{c.nombre}</div>
                    <div className="text-zinc-500">
                      {c.material_nombre} · {c.resultado?.cantidad_total} uds · <b className="text-amber-300">{c.resultado?.precio_final}€</b>
                      {c.cliente_nombre && <span className="ml-2 text-violet-300">🏢 {c.cliente_nombre}</span>}
                    </div>
                  </div>
                  {!c.peticion_id ? (
                    <Button size="sm" variant="ghost" onClick={() => convertir(c.id)}
                      title="Convertir en petición" className="h-7 text-xs">
                      <ArrowRight className="w-3 h-3 mr-1" /> Petición
                    </Button>
                  ) : (
                    <span className="text-emerald-400 text-[10px]">✓ Convertida</span>
                  )}
                  <Button size="sm" variant="ghost" onClick={() => eliminar(c.id)}
                    className="h-7 w-7 p-0 text-red-400"><Trash2 className="w-3 h-3" /></Button>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      {showAjustes && (
        <AjustesModal
          config={config}
          materiales={materiales}
          onClose={() => setShowAjustes(false)}
          onSaved={reloadConfig}
        />
      )}
    </div>
  );
}

// ---- Ajustes calculadora (mano de obra, corte unitario, PVP/m² por material) ----
function AjustesModal({ config, materiales, onClose, onSaved }) {
  const [cfg, setCfg] = useState({
    mano_obra_eur_hora: config?.mano_obra_eur_hora ?? 22,
    coste_corte_unitario_unit: config?.coste_corte_unitario_unit ?? 0.02,
    margen_pct_default: config?.margen_pct_default ?? 40,
    merma_pct_default: config?.merma_pct_default ?? 10,
    min_por_m2_produccion: config?.min_por_m2_produccion ?? 3.5,
  });
  const [mats, setMats] = useState(
    materiales.map((m) => ({
      ...m,
      _precio_venta_m2_edit: (m.precio_venta_m2 ?? Number(m.precio_m2 || m.precio_unitario || 0)) || 0,
    }))
  );
  const [busy, setBusy] = useState(false);

  const guardarGlobal = async () => {
    setBusy(true);
    try {
      await api.put("/calculadora-pegatinas/config", {
        mano_obra_eur_hora: Number(cfg.mano_obra_eur_hora),
        coste_corte_unitario_unit: Number(cfg.coste_corte_unitario_unit),
        margen_pct_default: Number(cfg.margen_pct_default),
        merma_pct_default: Number(cfg.merma_pct_default),
        min_por_m2_produccion: Number(cfg.min_por_m2_produccion),
      });
      toast.success("Ajustes globales guardados");
    } catch (err) { toast.error(err?.response?.data?.detail || "Error"); }
    setBusy(false);
  };

  const guardarMaterial = async (idx) => {
    const m = mats[idx];
    setBusy(true);
    try {
      await api.put(`/calculadora-pegatinas/materiales/${m.id}/precio`,
        { precio_venta_m2: Number(m._precio_venta_m2_edit) });
      toast.success(`${m.nombre} · ${Number(m._precio_venta_m2_edit).toFixed(2)}€/m²`);
    } catch (err) { toast.error(err?.response?.data?.detail || "Error"); }
    setBusy(false);
  };

  const guardarTodoYCerrar = async () => {
    await guardarGlobal();
    // Guardar todos los materiales cambiados
    for (let i = 0; i < mats.length; i += 1) {
      const orig = materiales[i]?.precio_venta_m2 ?? Number(materiales[i]?.precio_m2 || materiales[i]?.precio_unitario || 0);
      if (Number(mats[i]._precio_venta_m2_edit) !== Number(orig)) {
        // eslint-disable-next-line no-await-in-loop
        await guardarMaterial(i);
      }
    }
    if (onSaved) await onSaved();
    onClose();
  };

  return (
    <div className="fixed inset-0 bg-black/70 backdrop-blur-sm z-50 flex items-center justify-center p-4"
      onClick={onClose} data-testid="calc-ajustes-modal">
      <div className="bg-zinc-950 border border-zinc-800 rounded-xl max-w-3xl w-full max-h-[90vh] overflow-auto"
        onClick={(e) => e.stopPropagation()}>
        <div className="sticky top-0 bg-zinc-950 border-b border-zinc-800 p-4 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Settings className="w-5 h-5 text-amber-400" />
            <h3 className="font-semibold">Ajustes de la calculadora</h3>
          </div>
          <Button variant="ghost" size="sm" onClick={onClose}>✕</Button>
        </div>
        <div className="p-4 space-y-4">
          <div>
            <h4 className="text-sm font-semibold mb-2">Ajustes globales</h4>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
              <div>
                <Label className="text-xs">Mano de obra €/h</Label>
                <Input type="number" step="0.5" value={cfg.mano_obra_eur_hora}
                  onChange={(e) => setCfg({ ...cfg, mano_obra_eur_hora: e.target.value })}
                  data-testid="ajustes-mano-obra"
                  className="bg-zinc-900 border-zinc-800" />
              </div>
              <div>
                <Label className="text-xs">Coste corte unitario € por pieza</Label>
                <Input type="number" step="0.001" value={cfg.coste_corte_unitario_unit}
                  onChange={(e) => setCfg({ ...cfg, coste_corte_unitario_unit: e.target.value })}
                  data-testid="ajustes-corte"
                  className="bg-zinc-900 border-zinc-800" />
              </div>
              <div>
                <Label className="text-xs">Margen % default</Label>
                <Input type="number" step="1" value={cfg.margen_pct_default}
                  onChange={(e) => setCfg({ ...cfg, margen_pct_default: e.target.value })}
                  className="bg-zinc-900 border-zinc-800" />
              </div>
              <div>
                <Label className="text-xs">Merma % default</Label>
                <Input type="number" step="1" value={cfg.merma_pct_default}
                  onChange={(e) => setCfg({ ...cfg, merma_pct_default: e.target.value })}
                  className="bg-zinc-900 border-zinc-800" />
              </div>
              <div>
                <Label className="text-xs">Minutos producción por m²</Label>
                <Input type="number" step="0.1" value={cfg.min_por_m2_produccion}
                  onChange={(e) => setCfg({ ...cfg, min_por_m2_produccion: e.target.value })}
                  className="bg-zinc-900 border-zinc-800" />
              </div>
            </div>
          </div>

          <div className="border-t border-zinc-800 pt-4">
            <h4 className="text-sm font-semibold mb-2">Precio de venta por m² (por material)</h4>
            <p className="text-[11px] text-zinc-500 mb-2">
              Este precio ya INCLUYE material + impresión + corte estándar. Se multiplica por los m² usados en el cálculo.
            </p>
            <div className="space-y-1.5 max-h-[320px] overflow-y-auto pr-1">
              {mats.map((m, idx) => (
                <div key={m.id} className="flex items-center gap-2 bg-zinc-900/60 border border-zinc-800 rounded p-2 text-xs">
                  <div className="flex-1 min-w-0">
                    <div className="truncate font-medium">{m.nombre}</div>
                    <div className="text-[10px] text-zinc-500">{m.categoria || "—"}</div>
                  </div>
                  <Input type="number" step="0.01" min="0"
                    value={m._precio_venta_m2_edit}
                    onChange={(e) => setMats((arr) => arr.map((x, i) => i === idx ? { ...x, _precio_venta_m2_edit: e.target.value } : x))}
                    className="h-8 w-24 bg-zinc-950 border-zinc-800 text-right"
                    data-testid={`ajustes-mat-precio-${m.id}`} />
                  <span className="text-[10px] text-zinc-500 w-8">€/m²</span>
                  <Button size="sm" variant="ghost" onClick={() => guardarMaterial(idx)} disabled={busy}
                    className="h-7 w-7 p-0" title="Guardar sólo este">
                    <Check className="w-3.5 h-3.5 text-emerald-400" />
                  </Button>
                </div>
              ))}
            </div>
          </div>
        </div>
        <div className="sticky bottom-0 bg-zinc-950 border-t border-zinc-800 p-3 flex justify-end gap-2">
          <Button variant="ghost" onClick={onClose}>Cancelar</Button>
          <Button onClick={guardarTodoYCerrar} disabled={busy}
            className="bg-amber-500 text-zinc-950 hover:bg-amber-400"
            data-testid="ajustes-guardar-todo">
            <Save className="w-4 h-4 mr-1" /> Guardar todo
          </Button>
        </div>
      </div>
    </div>
  );
}

// ---- Preview visual bobina + resultados ----
function ResultadoCard({ r }) {
  // Layout SVG básico del primer diseño en la bobina
  const primero = r.disenos?.[0];
  const escala = 3; // px por cm
  const svg = useMemo(() => {
    if (!primero) return null;
    const ancho_svg = Math.min(700, r.ancho_bobina_cm * escala);
    // Aprovechamos ratio real
    const ratio_bobina = ancho_svg / (r.ancho_bobina_cm * escala);
    const alto_svg = Math.min(500, primero.largo_con_merma_cm * escala * ratio_bobina);
    const gutter_px = (r.gutter_mm / 10) * escala * ratio_bobina;
    const w = (primero.ancho_cm + r.gutter_mm / 10) * escala * ratio_bobina;
    const h = (primero.alto_cm + r.gutter_mm / 10) * escala * ratio_bobina;
    const filas = primero.filas;
    const cols = primero.piezas_por_fila;
    const rects = [];
    let count = 0;
    for (let f = 0; f < filas; f++) {
      for (let c = 0; c < cols; c++) {
        if (count >= primero.cantidad) break;
        rects.push(
          <rect key={`${f}-${c}`}
            x={c * w + gutter_px / 2}
            y={f * h + gutter_px / 2}
            width={w - gutter_px}
            height={h - gutter_px}
            fill="rgba(52, 211, 153, 0.5)"
            stroke="rgba(52, 211, 153, 0.9)"
            strokeWidth="0.5" />
        );
        count++;
      }
    }
    return { rects, ancho_svg, alto_svg };
  }, [primero, r]);

  return (
    <div className="bg-zinc-900/40 border border-zinc-800 rounded-xl p-4">
      <div className="grid md:grid-cols-2 gap-4">
        {/* Preview */}
        <div>
          <div className="text-xs text-zinc-500 mb-1">
            Aprovechamiento bobina · <span className="text-amber-300">{r.m2_total} m² usados</span>
          </div>
          {svg && (
            <div className="bg-zinc-950 border border-zinc-800 rounded p-2 overflow-hidden">
              <svg width={svg.ancho_svg} height={svg.alto_svg}
                viewBox={`0 0 ${svg.ancho_svg} ${svg.alto_svg}`}
                data-testid="calc-preview-svg"
                className="max-w-full">
                <rect width="100%" height="100%" fill="#18181b" />
                {svg.rects}
              </svg>
              <div className="text-[10px] text-zinc-600 mt-1">
                {primero.piezas_por_fila}/fila × {primero.filas} filas · {r.largo_total_cm} cm lineales
              </div>
            </div>
          )}
        </div>

        {/* Desglose */}
        <div>
          <div className="text-sm font-semibold mb-2">Presupuesto</div>
          <div className="space-y-1 text-xs">
            <Fila label="Impresión" v={r.costes.impresion}
              extra={r.precio_venta_m2 ? `${r.m2_total} m² × ${r.precio_venta_m2}€/m²` : `${r.m2_total} m²`} />
            <Fila label="Corte unitario" v={r.costes.corte_unitario} />
            <Fila label="Mano de obra" v={r.costes.mano_obra} extra={`${r.produccion_min} min`} />
            <div className="border-t border-zinc-800 my-1" />
            <Fila label="Coste total" v={r.costes.total} bold />
            <Fila label={`Margen ${r.margen_pct}%`} v={r.precio_venta_sin_descuento - r.costes.total} />
            {r.descuento_pct > 0 && (
              <Fila label={`Descuento -${r.descuento_pct}%`} v={-(r.precio_venta_sin_descuento - r.precio_final)}
                extra={`tramo ≥${r.cantidad_total}u`} />
            )}
            <div className="border-t border-amber-500/50 my-1" />
            <div className="flex items-center justify-between text-base font-bold text-amber-300">
              <span>💰 Precio final</span>
              <span data-testid="calc-precio-final">{r.precio_final} €</span>
            </div>
            <div className="text-[11px] text-zinc-500 text-right">
              {r.precio_unitario} €/u · {r.cantidad_total} pegatinas
            </div>
          </div>
          {/* Escenarios */}
          <div className="mt-3 pt-2 border-t border-zinc-800">
            <div className="text-[10px] text-zinc-500 mb-1">¿Y si sube la cantidad?</div>
            <div className="grid grid-cols-3 gap-1 text-[11px]">
              {r.escenarios.map((e, i) => (
                <div key={i} className="bg-zinc-950/70 rounded p-1.5 text-center">
                  <div className="text-zinc-400">{e.cantidad} uds</div>
                  <div className="text-amber-300 font-semibold">{e.precio_estimado}€</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function Fila({ label, v, extra, bold }) {
  return (
    <div className={`flex items-center justify-between ${bold ? "font-semibold" : ""}`}>
      <span className="text-zinc-400">{label}{extra && <span className="text-zinc-600 ml-1">({extra})</span>}</span>
      <span className={bold ? "text-zinc-100" : "text-zinc-300 font-mono"}>{Number(v).toFixed(2)} €</span>
    </div>
  );
}
